import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  Loader2,
  Briefcase,
  MapPin,
  User as UserIcon,
  Clock,
  ChevronRight,
  Filter as FilterIcon,
} from "lucide-react";
import { toast } from "sonner";
import { jobsApi, customersApi, techniciansApi } from "../../lib/api";
import { jobStatusColors } from "../../data/jobsMock";

/**
 * Mobile Jobs — single-column, large-tap-target list for the non-technician
 * mobile audience (admin, dispatcher).
 *
 * Tapping a job now navigates to `/mobile/jobs/:id` — the SAME full-screen
 * job page technicians use (status flow, checklist, tabs, Ask Guru). There
 * is a single unified mobile job detail experience for every role; only
 * this list view differs per audience. Desktop `/jobs` is unaffected.
 */

const PRIORITY_COLORS = {
  Urgent: { bg: "#FEE2E2", text: "#B91C1C" },
  High: { bg: "#FFE4E6", text: "#BE185D" },
  Normal: { bg: "#E0E7FF", text: "#3730A3" },
  Low: { bg: "#F1F5F9", text: "#475569" },
};

const FILTER_STATUSES = ["All", "Scheduled", "Dispatched", "En Route", "On Site", "In Progress", "Completed", "Unassigned", "Canceled"];

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" });
}
function fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
function fmtDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.toLocaleDateString([], { month: "short", day: "numeric" })} · ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
}

export default function MobileJobs() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [techs, setTechs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [techFilter, setTechFilter] = useState("All");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [j, c, t] = await Promise.all([jobsApi.list(), customersApi.list(), techniciansApi.list()]);
      setJobs(j || []); setCustomers(c || []); setTechs(t || []);
    } catch {
      setJobs([]); setCustomers([]); setTechs([]);
      toast.error("Could not load jobs");
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    loadAll().finally(() => setLoading(false));
  }, [loadAll]);

  // Instant refresh the instant a job is closed elsewhere (no manual reload needed).
  useEffect(() => {
    const onJobStatusChanged = () => loadAll();
    window.addEventListener("job-status-changed", onJobStatusChanged);
    return () => window.removeEventListener("job-status-changed", onJobStatusChanged);
  }, [loadAll]);

  const openDetail = (id) => navigate(`/mobile/jobs/${id}`);

  const techNames = useMemo(() => ["All", ...Array.from(new Set(techs.map((t) => t.name))), "Unassigned"], [techs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Jobs page shows only active (not-Completed) jobs — completed jobs still
    // exist and remain visible on the Job History page.
    return jobs
      .filter((j) => j.status !== "Completed")
      .filter((j) => {
        if (status !== "All") {
          if (status === "Unassigned") { if (j.tech && j.tech !== "Unassigned") return false; }
          else if (j.status !== status) return false;
        }
        if (techFilter !== "All") {
          if (techFilter === "Unassigned") { if (j.tech && j.tech !== "Unassigned") return false; }
          else if (j.tech !== techFilter) return false;
        }
        if (q) {
          const hay = `${j.customer || ""} ${j.address || ""} ${j.jobType || j.type || ""} ${j.tech || ""} ${j.id || ""}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => (a.start || "").localeCompare(b.start || ""));
  }, [jobs, status, techFilter, search]);

  const counts = useMemo(() => {
    const activeJobs = jobs.filter((j) => j.status !== "Completed");
    const c = { All: activeJobs.length, Unassigned: 0, Canceled: 0 };
    for (const j of activeJobs) {
      c[j.status] = (c[j.status] || 0) + 1;
      if (!j.tech || j.tech === "Unassigned") c.Unassigned += 1;
    }
    return c;
  }, [jobs]);

  return (
    <div
      className="mv md:hidden min-h-screen w-full"
      style={{
        color: "#0f172a",
        paddingTop: "calc(3.5rem + env(safe-area-inset-top))",
        paddingBottom: "calc(5rem + env(safe-area-inset-bottom))",
      }}
      data-testid="mobile-jobs"
    >
      {/* Search + new + filter toggle */}
      <div className="px-4 pt-3 pb-2 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2" color="#64748b" style={{ color: "#64748b", stroke: "#64748b" }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Customer, address, tech, #ID"
            data-testid="mobile-jobs-search"
            className="w-full h-11 pl-9 pr-3 rounded-full text-[15px]"
            style={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", color: "#0f172a" }}
          />
        </div>
        <button
          onClick={() => setFiltersOpen((v) => !v)}
          data-testid="mobile-jobs-filter"
          aria-label="Toggle filters"
          className="h-11 w-11 rounded-full flex items-center justify-center"
          style={{ backgroundColor: filtersOpen ? "#DBEAFE" : "#ffffff", border: "1px solid #e2e8f0" }}
        >
          <FilterIcon className="h-5 w-5" color={filtersOpen ? "#2563EB" : "#334155"} strokeWidth={2.25} style={{ color: filtersOpen ? "#2563EB" : "#334155", stroke: filtersOpen ? "#2563EB" : "#334155" }} />
        </button>
        <button
          onClick={() => window.dispatchEvent(new CustomEvent("open-new-job"))}
          data-testid="mobile-jobs-new"
          aria-label="New job"
          className="h-11 w-11 rounded-full flex items-center justify-center active:opacity-90"
          style={{ backgroundColor: "#2563EB" }}
        >
          <Plus className="h-5 w-5" color="#ffffff" strokeWidth={2.5} style={{ color: "#ffffff", stroke: "#ffffff" }} />
        </button>
      </div>

      {/* Status chips (horizontal scroll) */}
      <div className="px-4 pb-2 overflow-x-auto no-scrollbar">
        <div className="flex items-center gap-2 min-w-max">
          {FILTER_STATUSES.map((s) => {
            const active = status === s;
            const c = jobStatusColors[s];
            const count = counts[s] ?? 0;
            return (
              <button
                key={s}
                onClick={() => setStatus(s)}
                data-testid={`mobile-jobs-chip-${s.toLowerCase().replace(/\s+/g, "-")}`}
                className="h-9 px-3 rounded-full inline-flex items-center gap-1.5 text-[12.5px] font-semibold whitespace-nowrap"
                style={{
                  backgroundColor: active ? "#0B1E3F" : (c?.bg || "#ffffff"),
                  color: active ? "#ffffff" : (c?.text || "#334155"),
                  border: active ? "1px solid #0B1E3F" : "1px solid #e2e8f0",
                }}
              >
                {s} <span className="opacity-70">· {count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Optional filter drawer */}
      {filtersOpen && (
        <div className="mx-4 mb-2 p-3 rounded-xl" style={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0" }}>
          <div className="text-[11.5px] font-bold uppercase tracking-wide mb-1" style={{ color: "#64748b" }}>Assigned tech</div>
          <select
            value={techFilter}
            onChange={(e) => setTechFilter(e.target.value)}
            data-testid="mobile-jobs-tech-filter"
            className="w-full h-11 px-3 rounded-lg text-[14.5px]"
            style={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", color: "#0f172a" }}
          >
            {techNames.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      )}

      {loading ? (
        <div className="mt-6 flex items-center justify-center text-[14px]" style={{ color: "#64748b" }}>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading jobs…
        </div>
      ) : filtered.length === 0 ? (
        <div className="px-6 pt-16 text-center">
          <Briefcase className="h-10 w-10 mx-auto mb-3" color="#94a3b8" style={{ color: "#94a3b8", stroke: "#94a3b8" }} />
          <div className="text-[15px] font-semibold" style={{ color: "#0f172a" }}>No matching jobs</div>
          <div className="text-[13px] mt-1" style={{ color: "#64748b" }}>
            Try clearing filters or the search term.
          </div>
        </div>
      ) : (
        <ul className="px-4 mt-1 space-y-2" data-testid="mobile-jobs-list">
          {filtered.map((j) => <JobCard key={j.id} job={j} onOpen={() => openDetail(j.id)} />)}
        </ul>
      )}
    </div>
  );
}

function JobCard({ job, onOpen }) {
  const c = jobStatusColors[job.status] || jobStatusColors.Scheduled;
  const p = PRIORITY_COLORS[job.priority] || PRIORITY_COLORS.Normal;
  return (
    <li>
      <button
        onClick={onOpen}
        data-testid={`mobile-jobs-card-${job.id}`}
        className="w-full text-left rounded-2xl p-3 active:opacity-90 flex items-stretch gap-3"
        style={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0" }}
      >
        {/* Left status stripe with time chip */}
        <div className="w-16 shrink-0 flex flex-col items-center justify-center gap-1.5 rounded-xl py-1" style={{ backgroundColor: c.bg }}>
          <div className="text-[11px] font-bold uppercase tracking-wider" style={{ color: c.text }}>
            {job.start ? fmtDate(job.start) : "—"}
          </div>
          <div className="text-[15px] font-black leading-none" style={{ color: c.text }}>
            {job.start ? (fmtTime(job.start).split(" ")[0]) : "—"}
          </div>
          <div className="text-[10px] font-bold uppercase" style={{ color: c.text, opacity: 0.75 }}>
            {job.start ? (fmtTime(job.start).split(" ")[1] || "AM") : ""}
          </div>
        </div>

        <div className="min-w-0 flex-1 py-0.5">
          <div className="flex items-center gap-2">
            <div className="text-[15.5px] font-bold truncate flex-1" style={{ color: "#0f172a" }}>{job.customer || "—"}</div>
            <span className="text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0"
                  style={{ backgroundColor: c.bg, color: c.text }}>{job.status || "Scheduled"}</span>
          </div>
          <div className="text-[13px] mt-0.5 truncate" style={{ color: "#475569" }}>
            {job.jobType || job.type || "Service"}
          </div>
          {job.address && (
            <div className="text-[12.5px] mt-1 truncate flex items-center gap-1" style={{ color: "#64748b" }}>
              <MapPin className="h-3.5 w-3.5 shrink-0" color="#64748b" style={{ color: "#64748b", stroke: "#64748b" }} />
              {job.address}
            </div>
          )}
          <div className="flex items-center gap-2 mt-1.5">
            <div className="text-[12px] flex items-center gap-1 flex-1 truncate" style={{ color: "#334155" }}>
              <UserIcon className="h-3.5 w-3.5 shrink-0" color={job.tech ? "#334155" : "#f59e0b"} style={{ color: job.tech ? "#334155" : "#f59e0b", stroke: job.tech ? "#334155" : "#f59e0b" }} />
              <span className={job.tech ? "" : "font-semibold"} style={{ color: job.tech ? "#334155" : "#B45309" }}>{job.tech || "Unassigned"}</span>
            </div>
            {job.priority && (
              <span className="text-[10.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: p.bg, color: p.text }}>{job.priority}</span>
            )}
          </div>
        </div>
        <ChevronRight className="h-5 w-5 self-center shrink-0" color="#94a3b8" style={{ color: "#94a3b8", stroke: "#94a3b8" }} />
      </button>
    </li>
  );
}
